// Browser-side tar archive creator (USTAR format)
// Creates a valid tar blob from an array of {path, file} entries.

function encodeString(str: string, length: number): Uint8Array {
  const buf = new Uint8Array(length)
  const encoder = new TextEncoder()
  const encoded = encoder.encode(str)
  buf.set(encoded.subarray(0, length))
  return buf
}

function encodeOctal(value: number, length: number): Uint8Array {
  // Encode as zero-padded octal string with null terminator
  // The field is `length` bytes: (length-1) octal digits + null
  const octal = value.toString(8).padStart(length - 1, '0')
  const buf = new Uint8Array(length)
  const encoder = new TextEncoder()
  const encoded = encoder.encode(octal)
  buf.set(encoded.subarray(0, length - 1))
  buf[length - 1] = 0
  return buf
}

function calculateChecksum(header: Uint8Array): number {
  let sum = 0
  for (let i = 0; i < 512; i++) {
    // During checksum calculation, the checksum field (bytes 148-155) is treated as spaces
    if (i >= 148 && i < 156) {
      sum += 0x20 // space
    } else {
      sum += header[i]
    }
  }
  return sum
}

function writeChecksum(header: Uint8Array, checksum: number): void {
  // Checksum field: 6-digit zero-padded octal + null + space (bytes 148-155)
  const octal = checksum.toString(8).padStart(6, '0')
  const encoder = new TextEncoder()
  const encoded = encoder.encode(octal)
  header.set(encoded.subarray(0, 6), 148)
  header[154] = 0   // null
  header[155] = 0x20 // space
}

function buildHeader(filePath: string, fileSize: number): Uint8Array {
  const header = new Uint8Array(512)
  const now = Math.floor(Date.now() / 1000)

  // Handle long paths using USTAR prefix field
  let name = filePath
  let prefix = ''

  if (filePath.length > 100) {
    // Split path: prefix gets directory portion, name gets filename portion
    const lastSlash = filePath.lastIndexOf('/')
    if (lastSlash > 0 && lastSlash <= 155) {
      prefix = filePath.substring(0, lastSlash)
      name = filePath.substring(lastSlash + 1)
    }
    // If name is still too long, truncate (edge case)
    if (name.length > 100) {
      name = name.substring(0, 100)
    }
  }

  // Name (bytes 0-99)
  header.set(encodeString(name, 100), 0)

  // Mode (bytes 100-107): 0644 for regular files
  header.set(encodeString('0000644\0', 8), 100)

  // UID (bytes 108-115): 1000 in octal = 1750
  header.set(encodeString('0001750\0', 8), 108)

  // GID (bytes 116-123): 1000 in octal = 1750
  header.set(encodeString('0001750\0', 8), 116)

  // Size (bytes 124-135): file size in octal
  header.set(encodeOctal(fileSize, 12), 124)

  // Mtime (bytes 136-147): current timestamp in octal
  header.set(encodeOctal(now, 12), 136)

  // Checksum placeholder (bytes 148-155): 8 spaces
  for (let i = 148; i < 156; i++) {
    header[i] = 0x20
  }

  // Type flag (byte 156): '0' for regular file
  header[156] = 0x30

  // USTAR magic (bytes 257-262): 'ustar\0'
  header.set(encodeString('ustar\0', 6), 257)

  // Version (bytes 263-264): '00'
  header[263] = 0x30 // '0'
  header[264] = 0x30 // '0'

  // Prefix (bytes 345-499): directory portion for long paths
  if (prefix) {
    header.set(encodeString(prefix, 155), 345)
  }

  // Calculate and write checksum
  const checksum = calculateChecksum(header)
  writeChecksum(header, checksum)

  return header
}

function toBuffer(arr: Uint8Array): ArrayBuffer {
  // Copy into a fresh ArrayBuffer to avoid SharedArrayBuffer type issues
  const buf = new ArrayBuffer(arr.byteLength)
  new Uint8Array(buf).set(arr)
  return buf
}

export async function createTarArchive(
  files: { path: string; file: File }[],
): Promise<Blob> {
  const parts: ArrayBuffer[] = []

  for (const entry of files) {
    const content = new Uint8Array(await entry.file.arrayBuffer())
    const header = buildHeader(entry.path, content.length)

    parts.push(toBuffer(header))
    parts.push(toBuffer(content))

    // Pad to 512-byte boundary
    const remainder = content.length % 512
    if (remainder > 0) {
      parts.push(new ArrayBuffer(512 - remainder))
    }
  }

  // End-of-archive marker: two 512-byte blocks of zeros
  parts.push(new ArrayBuffer(1024))

  return new Blob(parts, { type: 'application/x-tar' })
}
